import { ReactNode, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

interface AuthRequiredProps {
  children: ReactNode;
}

interface User {
  id: number;
  email: string;
}

export default function AuthRequired({ children }: AuthRequiredProps) {
  const [, navigate] = useLocation();
  
  // Fetch the current user
  const { data: user, isLoading, error } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    retry: 1,
  });

  // Redirect to auth page if not logged in
  useEffect(() => {
    if (!isLoading && !user && !error) {
      navigate("/auth");
    }
  }, [user, isLoading, error, navigate]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="h-10 w-10 animate-spin text-red-600" />
      </div>
    );
  }

  // If we have a user, render the children
  if (user) {
    return <>{children}</>;
  }

  // This should not be visible as the useEffect should redirect,
  // but we'll return null as a fallback
  return null;
}
